import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, Download, ChevronDown, ChevronRight, CreditCard } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  paid: 'bg-green-500/20 text-green-300 border-green-500/50',
  pending: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50',
  failed: 'bg-red-500/20 text-red-300 border-red-500/50',
  expired: 'bg-red-500/20 text-red-300 border-red-500/50',
  refunded: 'bg-blue-500/20 text-blue-300 border-blue-500/50',
};

const STATUS_LABEL = {
  paid: 'Payé',
  pending: 'En attente',
  failed: 'Échec',
  expired: 'Expiré',
  refunded: 'Remboursé',
};

const csvEscape = (val) => {
  if (val === null || val === undefined) return '';
  const s = String(val).replace(/"/g, '""');
  return /[",\n;]/.test(s) ? `"${s}"` : s;
};

const formatCurrency = (amountCents, currency) => {
  if (!amountCents && amountCents !== 0) return '—';
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: (currency || 'eur').toUpperCase(),
    }).format(amountCents / 100);
  } catch {
    return `${(amountCents / 100).toFixed(2)} ${(currency || 'EUR').toUpperCase()}`;
  }
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const PaymentsAdmin = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all'); // all | paid | pending | failed | refunded

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${API_URL}/api/admin/payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`Erreur ${resp.status}`);
      setData(await resp.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const filtered = useMemo(() => {
    const list = data?.transactions || [];
    if (filter === 'all') return list;
    return list.filter((t) => t.payment_status === filter);
  }, [data, filter]);

  const exportCsv = () => {
    const headers = ['Date', 'Service', 'Montant', 'Devise', 'Client', 'Email', 'Statut', 'Session ID'];
    const rows = filtered.map((t) => [
      csvEscape(formatDate(t.created_at)),
      csvEscape(t.service_label),
      csvEscape(((t.amount || 0) / 100).toFixed(2)),
      csvEscape(t.currency),
      csvEscape(t.customer_name),
      csvEscape(t.customer_email),
      csvEscape(STATUS_LABEL[t.payment_status] || t.payment_status),
      csvEscape(t.session_id),
    ]);
    const csv = [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `paiements-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = data?.stats || {};

  return (
    <div className="bg-[#302f2c] border border-[#3f4816] rounded-xl overflow-hidden mb-6" data-testid="payments-admin">
      {/* Header */}
      <div className="p-4 border-b border-[#3f4816] flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CreditCard className="text-[#d9fb06]" size={20} />
          <h2 className="text-lg font-bold text-[#dfddd6]">
            Paiements <span className="text-[#888680]">({data?.transactions?.length || 0})</span>
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchPayments}
            disabled={loading}
            className="flex items-center gap-2 text-sm border border-[#3f4816] text-[#dfddd6] px-3 py-2 rounded-lg hover:border-[#d9fb06] hover:text-[#d9fb06] transition-colors disabled:opacity-50"
            data-testid="payments-refresh-btn"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
          <button
            onClick={exportCsv}
            disabled={!filtered.length}
            className="flex items-center gap-2 text-sm bg-[#d9fb06] text-[#1a1c1b] font-bold px-3 py-2 rounded-lg hover:bg-[#d9fb06]/90 disabled:opacity-50"
            data-testid="payments-export-btn"
          >
            <Download size={14} />
            CSV
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3 border-b border-[#3f4816]" data-testid="payments-stats">
        <StatCard label="Total" value={stats.total || 0} />
        <StatCard label="Payés" value={stats.paid || 0} highlight />
        <StatCard label="En attente" value={stats.pending || 0} />
        <StatCard label="Échoués" value={stats.failed || 0} />
        <StatCard
          label="Revenus"
          value={formatCurrency(stats.total_revenue_cents, stats.currency)}
          highlight
          data-testid="payments-revenue"
        />
      </div>

      {/* Filters */}
      <div className="p-4 border-b border-[#3f4816] flex flex-wrap gap-2">
        {['all', 'paid', 'pending', 'failed', 'refunded'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase transition-colors ${
              filter === f
                ? 'bg-[#d9fb06] text-[#1a1c1b]'
                : 'bg-[#1a1c1b] text-[#888680] border border-[#3f4816] hover:text-[#d9fb06] hover:border-[#d9fb06]'
            }`}
            data-testid={`payments-filter-${f}`}
          >
            {f === 'all' ? 'Tous' : STATUS_LABEL[f] || f}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="max-h-[600px] overflow-y-auto">
        {loading && (
          <div className="p-8 flex items-center justify-center text-[#888680]">
            <Loader2 className="animate-spin mr-2" size={18} /> Chargement…
          </div>
        )}
        {error && (
          <div className="p-6 text-red-400 text-sm" data-testid="payments-error">
            {error}
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div className="p-8 text-center text-[#888680]" data-testid="payments-empty">
            Aucun paiement pour ce filtre.
          </div>
        )}
        {!loading && !error && filtered.map((t) => {
          const isOpen = expandedId === t.session_id;
          return (
            <div
              key={t.session_id}
              className="border-b border-[#3f4816] last:border-b-0"
              data-testid={`payment-row-${t.session_id}`}
            >
              <button
                onClick={() => setExpandedId(isOpen ? null : t.session_id)}
                className="w-full p-4 flex flex-wrap items-center justify-between gap-3 hover:bg-[#1a1c1b]/40 text-left transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {isOpen ? <ChevronDown size={16} className="text-[#d9fb06]" /> : <ChevronRight size={16} className="text-[#888680]" />}
                  <div className="min-w-0">
                    <p className="text-[#dfddd6] font-semibold truncate">{t.service_label}</p>
                    <p className="text-xs text-[#888680]">{formatDate(t.created_at)} · {t.customer_email || 'invité'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[#d9fb06] font-bold">{formatCurrency(t.amount, t.currency)}</span>
                  <span className={`text-xs px-2 py-1 rounded-full border ${STATUS_BADGE[t.payment_status] || 'bg-gray-500/20 text-gray-300 border-gray-500/50'}`}>
                    {STATUS_LABEL[t.payment_status] || t.payment_status}
                  </span>
                </div>
              </button>
              {isOpen && (
                <div className="p-4 pt-0 pl-11 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <DetailRow label="Client" value={t.customer_name || '—'} />
                  <DetailRow label="Email" value={t.customer_email || '—'} />
                  <DetailRow label="Session Stripe" value={t.session_id} mono />
                  <DetailRow label="Payment Intent" value={t.stripe_payment_intent_id || '—'} mono />
                  <DetailRow label="Quantité" value={t.quantity || 1} />
                  <DetailRow label="Mis à jour" value={formatDate(t.updated_at)} />
                  {t.emails_sent && <DetailRow label="Emails envoyés" value="Oui" />}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, highlight, ...rest }) => (
  <div className="bg-[#1a1c1b] border border-[#3f4816] rounded-lg p-3" {...rest}>
    <p className="text-xs text-[#888680] uppercase font-bold tracking-wide">{label}</p>
    <p className={`mt-1 text-lg font-black ${highlight ? 'text-[#d9fb06]' : 'text-[#dfddd6]'}`}>{value}</p>
  </div>
);

const DetailRow = ({ label, value, mono }) => (
  <div className="flex items-baseline gap-2">
    <span className="text-[#888680] text-xs uppercase min-w-[110px]">{label}</span>
    <span className={`text-[#dfddd6] ${mono ? 'font-mono text-xs break-all' : ''}`}>{value}</span>
  </div>
);

export default PaymentsAdmin;
